/**
 * R232q5s: Proximity NPC interaction prompt.
 *
 * Когда игрок в пределах ~6 м от server-spawned NPC (state.npcs из net-store),
 * рисует heraldic нашлёп «[F] Заговорить с N» в нижней части HUD. F открывает
 * ServerNpcDialog (через aiwari-server-npc-open custom event).
 *
 * Это temporary bridge до тех пор пока World.tsx не получит NPC mesh rendering
 * с полноценным click handler (Phase 2 NPC visuals task). На сейчас — самый
 * быстрый способ сделать NPC interactable без переписывания 2700-LOC World.tsx.
 */

import { useEffect, useState } from 'react';
import { useNet } from '../state/net-store.js';

const INTERACT_RANGE_M = 6;

export function NpcInteractPrompt() {
  const npcs = useNet((s) => s.npcs);
  const players = useNet((s) => s.players);
  const selfId = useNet((s) => s.selfId);
  const [closest, setClosest] = useState<{ id: string; name: string; subtitle: string; type: string; dist: number } | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (!selfId) { setClosest(null); return; }
      // R232q86zdg: read self-pos from actual scene rig, not net-store.
      // net-store player.pos can be stale (0,0,0) when Colyseus Schema
      // sync hasn't propagated the nested PositionSchema yet. The rig
      // position is authoritative — server sends a one-shot self-pos
      // event that World.tsx applies directly to selfRig.root.position.
      const w = window as unknown as { __aiwariSelfRig?: { root?: { position: { x: number; z: number } } } };
      const rigPos = w.__aiwariSelfRig?.root?.position;
      const meX = rigPos?.x ?? players[selfId]?.pos?.x ?? 0;
      const meZ = rigPos?.z ?? players[selfId]?.pos?.z ?? 0;
      // Skip detection entirely if rig is still at origin (not yet spawned).
      if (Math.abs(meX) < 10 && Math.abs(meZ) < 10) { setClosest(null); return; }
      let best: { id: string; name: string; subtitle: string; type: string; dist: number } | null = null;
      for (const npc of Object.values(npcs)) {
        // Skip NPCs at origin — those are config drift, not real positions.
        if (Math.abs(npc.pos.x) < 10 && Math.abs(npc.pos.z) < 10) continue;
        const dx = npc.pos.x - meX;
        const dz = npc.pos.z - meZ;
        const d = Math.hypot(dx, dz);
        if (d > INTERACT_RANGE_M) continue;
        if (!best || d < best.dist) {
          best = { id: npc.id, name: npc.displayName, subtitle: npc.subtitle, type: npc.npcType, dist: d };
        }
      }
      setClosest(best);
    }, 250);
    return () => window.clearInterval(id);
  }, [npcs, players, selfId]);

  // F to interact. Flightmasters → flight picker; others → dialogue.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'f' && e.key !== 'F' && e.key !== 'а' && e.key !== 'А') return;
      // Don't fire if user is typing in an input.
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (!closest) return;
      e.preventDefault();
      const eventName = closest.type === 'flightmaster' ? 'aiwari-server-flight-open' : 'aiwari-server-npc-open';
      window.dispatchEvent(new CustomEvent(eventName, { detail: { npcId: closest.id } }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closest]);

  if (!closest) return null;

  return (
    <div
      // R232q86zC: lift NPC prompt ABOVE every action-bar layer.
      // Main bar bottom=16 + height 80 = 96. Bottom-left extra bottom=100
      // + height 50 = 150. Bottom-right bottom=152 + 50 = 202. So we
      // need ≥ 220 to clear all of them. z-index bumped to 70 so the
      // prompt also sits above CastBar (z 35) and any modal overlays
      // that open while talking.
      className="fixed left-1/2 -translate-x-1/2 pointer-events-none"
      style={{
        // R232q86zG: merged single style block. Previous version had TWO
        // style={{}} props — the second silently overrode the first,
        // wiping the bottom/zIndex lift that keeps the prompt above
        // ActionBar layers (z 35-50). That regression let the parchment
        // overlap the bars again. Now: parchment + lift in one object.
        position: 'fixed',
        bottom: 220,
        zIndex: 70,
        background: `
          linear-gradient(180deg, rgba(232,220,192,0.97), rgba(212,194,156,0.97)),
          radial-gradient(circle at 30% 20%, rgba(255,255,255,0.18), transparent 50%)`,
        border: '2px solid #5C4520',
        borderRadius: '6px',
        boxShadow: `
          0 6px 22px rgba(0,0,0,0.55),
          inset 0 0 22px rgba(92,69,32,0.22),
          inset 0 1px 0 rgba(255,255,255,0.45),
          0 0 0 4px rgba(250,179,0,0.20)`,
        padding: '14px 28px',
        fontFamily: 'EB Garamond, Georgia, serif',
        color: '#3A2818',
        minWidth: '280px',
        textAlign: 'center',
      }}
    >
      {/* Gold corner ornaments */}
      {[
        { top: -2, left: -2, br: 'tl' as const },
        { top: -2, right: -2, br: 'tr' as const },
        { bottom: -2, left: -2, br: 'bl' as const },
        { bottom: -2, right: -2, br: 'br' as const },
      ].map((c, i) => (
        <span
          key={i}
          aria-hidden
          style={{
            position: 'absolute', width: 14, height: 14,
            top: c.top, left: c.left, right: c.right, bottom: c.bottom,
            borderTop: c.br === 'tl' || c.br === 'tr' ? '2px solid #FAB300' : 'none',
            borderBottom: c.br === 'bl' || c.br === 'br' ? '2px solid #FAB300' : 'none',
            borderLeft: c.br === 'tl' || c.br === 'bl' ? '2px solid #FAB300' : 'none',
            borderRight: c.br === 'tr' || c.br === 'br' ? '2px solid #FAB300' : 'none',
          }}
        />
      ))}
      <div
        className="text-[11px] uppercase tracking-[0.3em] mb-1"
        style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
      >
        Поблизости
      </div>
      <div
        className="text-[18px] mb-1"
        style={{ fontFamily: 'Cinzel, serif', fontWeight: 600, color: '#3A2818' }}
      >
        {closest.name}
      </div>
      {closest.subtitle && (
        <div className="text-[12px] italic mb-2" style={{ color: '#5C4520' }}>
          {closest.subtitle}
        </div>
      )}
      <div
        className="inline-block px-3 py-1 text-[12px]"
        style={{
          background: 'linear-gradient(180deg, #FFD479, #C9A96A 50%, #8A6B2E)',
          color: '#0B0907',
          fontFamily: 'Spectral SC, serif',
          fontWeight: 700,
          letterSpacing: '0.15em',
          border: '1px solid #5C4520',
          borderRadius: '3px',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.4)',
        }}
      >
        [F] Заговорить
      </div>
    </div>
  );
}
